import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import { AbTitle, AbDescription, AbButton } from '../components/ui';
import FormFieldWrapper from './components/FormFieldWrapper.vue';
import StepsNavigation from './components/StepsNavigation.vue';
import ErrorMessage from './components/ErrorMessage.vue';
import FormFieldRecaptcha from './components/FormFieldRecaptcha.vue';
import type { FormData, Field } from './types';
import { useLocalization } from './composables/useLocalization';
import { useFormValues } from './composables/useFormValues';
import { useFormValidation } from './composables/useFormValidation';
import { fieldComponents } from './mappings/fieldComponents';
import { DEFAULT_TEXTS } from './constants';
import { FIELDS_WITH_DEPENDENT_FIELDS } from '../api/formsApi';


export default /*#__PURE__*/_defineComponent({
  __name: 'FormBuilder',
  props: {
    formData: null,
    isSubmitting: { type: Boolean },
    recaptchaConfig: null,
    hideFormTitle: { type: [Boolean, null] },
    hideFormDescription: { type: [Boolean, null] }
  },
  emits: ["submit", "dependency-field-input", "form-start"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'submit', values: Record<string, any>): void;
  (
    e: 'dependency-field-input',
    stepIndex: number,
    dependentFieldName: string,
    dependencyFieldValue: string,
  ): void;
  (e: 'form-start'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  formData: FormData;
  isSubmitting?: boolean;
  recaptchaConfig?: {
    siteKey: string;
    theme?: string;
    size?: string;
  };
  hideFormTitle?: boolean | null;
  hideFormDescription?: boolean | null;
};





const currentStepIndex = ref(0);
const formStarted = ref(false);
const currentStep = computed(
  () => props.formData.steps[currentStepIndex.value],
);

const { formValues, getInitialValue } = useFormValues(props.formData);
const {
  validateStep,
  validateAll,
  getFieldError,
  hasFieldError,
  stepsValidationState,
} = useFormValidation(props.formData, formValues);
const { getValidationMessage } = useLocalization();

const recaptchaError = ref<string | false>(false);

function onRecaptchaInput(val: string) {
  formValues.recaptcha = val;
  if (val) recaptchaError.value = false;
}

const setStep = (index: number) => {
  currentStepIndex.value = index;
};

const nextStep = async () => {
  const isValid = await validateStep(currentStepIndex.value);
  if (isValid && currentStepIndex.value < props.formData.steps.length - 1) {
    currentStepIndex.value++;
  }
};

const prevStep = () => {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
  }
};

const getFieldWidth = (field: Field) => {
  return field.type === 'hidden' ? undefined : field.width;
};

const wrapFieldProps = (value: any) => {
  if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
    return value;
  }
  return { value };
};

const getFieldByName = (fieldName: string): Field | undefined => {
  return currentStep.value.fields.find((field) => field.name === fieldName);
};

const handleDependencyFieldInput = (
  fieldValue: any,
  dependencyField: Field,
) => {
  const dependencyValue = fieldValue;
  const dependentFieldName = FIELDS_WITH_DEPENDENT_FIELDS[dependencyField.name];
  const dependentField = getFieldByName(dependentFieldName);

  if (!dependentField) return;
  formValues[dependentFieldName] = getInitialValue(dependentField);

  emit(
    'dependency-field-input',
    currentStepIndex.value,
    FIELDS_WITH_DEPENDENT_FIELDS[dependencyField.name],
    dependencyValue,
  );
};

const onInput = (value: any, field: Field) => {
  formValues[field.name] = value;
  if (!formStarted.value) {
    formStarted.value = true;
    emit('form-start');
  }
  if (field.name in FIELDS_WITH_DEPENDENT_FIELDS) {
    handleDependencyFieldInput(value, field);
  }
};

const onSubmit = async () => {
  const isValid = await validateAll();

  if (
    props.recaptchaConfig &&
    currentStepIndex.value === props.formData.steps.length - 1 &&
    !formValues.recaptcha
  ) {
    recaptchaError.value =
      getValidationMessage('recaptcha_required') ||
      'Please confirm that you are not a robot';
    return;
  }

  if (isValid) {
    emit('submit', { ...formValues });
  }
};

return { __sfc: true,props, emit, currentStepIndex, formStarted, currentStep, formValues, getInitialValue, validateStep, validateAll, getFieldError, hasFieldError, stepsValidationState, getValidationMessage, recaptchaError, onRecaptchaInput, setStep, nextStep, prevStep, getFieldWidth, wrapFieldProps, getFieldByName, handleDependencyFieldInput, onInput, onSubmit, AbTitle, AbDescription, AbButton, FormFieldWrapper, StepsNavigation, ErrorMessage, FormFieldRecaptcha, fieldComponents, DEFAULT_TEXTS }
}

})